import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearAuthTokens, getAccessToken } from "../api/client";
import { whoAmI } from "../api/services/clinic/account";
import { isInClient } from "@liff/is-in-client";
import { isLiffRoutePath } from "../utils/liffRoute";
import { getRequiredActionRoute } from "../utils/staffRequiredAction";
import { Branch, UserProfile } from "../api/services/clinic/account.types";
import { handleUserCurrentClinicBranch } from "../utils/authRedirect";

interface UserState {
    id: number | null;
    full_name: string;
    role: string;
    accessible_pages: string[];
    current_clinic_branch?: Branch | null;
    clinic_branches?: Branch[];
}

interface UserContextValue {
    user: UserState | null;
    setUser: React.Dispatch<React.SetStateAction<UserState | null>>;
    requiredAction: string | null;
    setRequiredAction: React.Dispatch<React.SetStateAction<string | null>>;
    loading: boolean;
    error: unknown;
}

const UserContext = createContext<UserContextValue | null>(null);
const FRONTEND_LOGIN_PATH = "/account/login";
const BRANCH_SELECTION_PATH = "/account/branch-selection";

export function UserProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<UserState | null>(null);
    const [requiredAction, setRequiredAction] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const pathname = location.pathname ?? "";
        const isLiffRoute = isLiffRoutePath(pathname);
        const isFrontendLoginRoute = pathname === FRONTEND_LOGIN_PATH;
        const isBranchSelectionRoute = pathname === BRANCH_SELECTION_PATH;
        const isTelemedJoinRoute = pathname.includes("/telemed/join/");

        if (isLiffRoute || isFrontendLoginRoute || isBranchSelectionRoute || isTelemedJoinRoute) {
            setLoading(false);
            return;
        }

        if (!getAccessToken()) {
            setUser(null);
            setRequiredAction(null);
            setLoading(false);
            navigate(FRONTEND_LOGIN_PATH, { replace: true });
            return;
        }

        if (user) {
            const requiredActionRoute = getRequiredActionRoute(requiredAction);
            if (requiredActionRoute && pathname !== requiredActionRoute) {
                navigate(requiredActionRoute, { replace: true });
                setLoading(false);
                return;
            }

            if (!requiredActionRoute && !user.current_clinic_branch && !isLiffRoute && !isBranchSelectionRoute) {
                const updateBranch = async () => {
                    try {
                        const currentClinicBranch = await handleUserCurrentClinicBranch(user as UserProfile, navigate);
                        if (currentClinicBranch) {
                            setUser((prev) => ({ ...(prev ?? {} as UserState), current_clinic_branch: currentClinicBranch }));
                        }
                    } finally {
                        setLoading(false);
                    }
                };
                updateBranch();
                return;
            }

            setLoading(false);
            return;
        }

        const fetchWhoAmI = async () => {
            try {
                setLoading(true);
                setError(null);

                const res = await whoAmI();
                const data: UserState = {
                    id: res?.id ?? null,
                    full_name: res?.full_name ?? "",
                    role: res?.role ?? "",
                    accessible_pages: Array.isArray(res?.accessible_pages) ? res.accessible_pages : [],
                    current_clinic_branch: res?.current_clinic_branch,
                    clinic_branches: res?.clinic_branches || [],
                };
                setUser(data);
                setRequiredAction(res?.required_action ?? null);

                const requiredActionRoute = getRequiredActionRoute(res?.required_action);
                if (requiredActionRoute && pathname !== requiredActionRoute) {
                    navigate(requiredActionRoute, { replace: true });
                    return;
                }

                if (!requiredActionRoute && !data.current_clinic_branch && !isLiffRoute && !isBranchSelectionRoute) {
                    const currentClinicBranch = await handleUserCurrentClinicBranch(data as UserProfile, navigate);
                    if (currentClinicBranch) {
                        setUser((prev) => ({ ...(prev ?? {} as UserState), current_clinic_branch: currentClinicBranch }));
                    }
                }
            } catch (err) {
                setError(err);
                setUser(null);
                setRequiredAction(null);
                const apiErr = err as { status?: number };
                if (apiErr?.status === 401) {
                    clearAuthTokens();
                    if (location.pathname !== FRONTEND_LOGIN_PATH) {
                        navigate(FRONTEND_LOGIN_PATH, { replace: true });
                    }
                    return;
                }
            } finally {
                setLoading(false);
            }
        };
        const isInLiff = isInClient();
        if (!isInLiff) {
            fetchWhoAmI();
        } else {
            setLoading(false);
        }
    }, [location.pathname, navigate, user, requiredAction]);

    return (
        <UserContext.Provider value={{
            user,
            setUser,
            requiredAction,
            setRequiredAction,
            loading,
            error,
        }}>
            {children}
        </UserContext.Provider>
    );
}

export function useUser(): UserContextValue {
    const ctx = useContext(UserContext);
    if (!ctx) {
        throw new Error("useUser must be used within a UserProvider");
    }
    return ctx;
}
